import { ExtractIngredientsResponseDto } from "../api";
import Food from "../data/food";
import { FoodUnit } from "../data/foodUnit";
import { getDefaultFoodUnit } from "./foodUtils";

/** Anzeige-Labels für die vom Backend gelieferten UnitType-Enum-Namen. */
export const UNIT_TYPE_LABELS: Record<
  NonNullable<ExtractIngredientsResponseDto["unit"]>,
  string
> = {
  GRAMM: "g",
  PORTION: "Portion",
  STUECK: "Stück",
  MILLILITER: "ml",
  PACKUNG: "Packung",
  PIZZA: "Pizza",
  TASSE: "Tasse",
  ESSLOEFFEL: "EL",
  GROSS_CAP: "Groß",
  BECHER: "Becher",
  SCHREIBE: "Schreibe",
  BROETCHEN: "Brötchen",
  KLEIN: "Klein",
  FLASCHE: "Flasche",
  TAFEL: "Tafel",
  TEELOEFFEL: "TL",
  MESSERSPITZE: "Msp.",
  PRISE: "Prise",
  SCHUSS: "Schuss",
  TROPFEN: "Tropfen",
  BUND: "Bund",
  ZEHE: "Zehe",
  KNOLLE: "Knolle",
  STANGE: "Stange",
};

/** Liefert das Anzeige-Label eines UnitType (robust gegen Enum-Name oder Wert). */
export function unitTypeLabel(
  unit: ExtractIngredientsResponseDto["unit"] | undefined,
): string | undefined {
  if (!unit) return undefined;
  return (UNIT_TYPE_LABELS as Record<string, string>)[unit] ?? unit;
}

/** Formatiert die extrahierte Menge inkl. Einheit für die Anzeige. */
export function formatExtractedQuantity(
  ingredient: ExtractIngredientsResponseDto,
): string {
  const unit = unitTypeLabel(ingredient.unit);
  if (ingredient.quantityNumeric != null) {
    return [ingredient.quantityNumeric, unit].filter(Boolean).join(" ");
  }
  return ingredient.quantity ?? "";
}

/**
 * Wählt für eine extrahierte Zutat die passende FoodUnit des gemappten Foods
 * und rechnet die Menge bestmöglich in diese Einheit um (Grundlage für die
 * anschließende, vom Nutzer anpassbare Menge).
 */
export function matchExtractedIngredient(
  food: Food,
  ingredient: ExtractIngredientsResponseDto,
): { foodUnit: FoodUnit | undefined; amount: number } {
  const units = food.foodUnits ?? [];
  const quantity = ingredient.quantityNumeric ?? 0;
  if (units.length === 0) {
    return { foodUnit: undefined, amount: quantity };
  }

  const unitLabel = unitTypeLabel(ingredient.unit);
  const exact = unitLabel
    ? units.find(
        (u) => u.description?.trim().toLowerCase() === unitLabel.toLowerCase(),
      )
    : undefined;
  const foodUnit = exact ?? getDefaultFoodUnit(food) ?? units[0];

  let amount = quantity;
  const netWeight = foodUnit.netWeight ?? 0;
  if (!exact && netWeight > 1) {
    amount = quantity / netWeight;
  }
  return { foodUnit, amount: Math.round(amount * 10) / 10 };
}
